import {
    ArrowLeftIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    PlusIcon,
    UserCircleIcon,
} from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import ROUTES from '@/ROUTES'

import { api } from '@/services/api.service'

import { cn } from '@/utils/cn'

import { useProviderSession } from './ProviderSession'

export interface HomeworkItem {
    _id: string
    title: string
    description: string | null
    dueDate: string | null
    status: 'assigned' | 'completed' | 'cancelled'
    assignedAt: string | null
    assignedByName: string | null
    completedAt: string | null
    completionNote: string | null
}

interface HomeworkListResponse {
    consented: boolean
    childName: string | null
    items: HomeworkItem[]
}

// Overdue is derived on read so it never goes stale (it is not a stored status).
type DisplayStatus = 'assigned' | 'completed' | 'overdue'

function displayStatus(item: HomeworkItem): DisplayStatus {
    if (item.status === 'completed') return 'completed'
    if (item.dueDate && new Date(item.dueDate).valueOf() < Date.now()) return 'overdue'
    return 'assigned'
}

function formatDate(value?: string | null) {
    if (!value) return null
    const d = new Date(value)
    if (isNaN(d.valueOf())) return null
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function ProviderFamilyHomework() {
    const session = useProviderSession()
    // Viewer is read-only: no assigning, no cancelling.
    const canManage = session?.role !== 'viewer'
    const { id } = useParams()
    const [data, setData] = useState<HomeworkListResponse | null>(null)
    const [loading, setLoading] = useState(true)
    const [notFound, setNotFound] = useState(false)
    const [showNew, setShowNew] = useState(false)

    useEffect(() => {
        let cancelled = false
        async function load() {
            const response = await api<HomeworkListResponse>(`${ROUTES.provider.homework}/${id}`)
            if (cancelled) return
            if (response.status && response.status < 400 && response.data) {
                setData(response.data)
            } else {
                setNotFound(true)
            }
            setLoading(false)
        }
        if (id) load()
        return () => {
            cancelled = true
        }
    }, [id])

    function handleAssigned(item: HomeworkItem) {
        setData((prev) => (prev ? { ...prev, items: [item, ...prev.items] } : prev))
        setShowNew(false)
    }

    function handleCancelled(homeworkId: string) {
        setData((prev) =>
            prev ? { ...prev, items: prev.items.filter((i) => i._id !== homeworkId) } : prev
        )
    }

    if (loading) {
        return <div className='max-w-5xl mx-auto px-4 py-12 text-center text-sm text-neutral-500'>Loading…</div>
    }

    if (notFound || !data) {
        return (
            <div className='max-w-3xl mx-auto px-4 py-12 text-center'>
                <p className='text-neutral-600'>Client not found.</p>
                <Link to='/clients' className='text-brand-600 hover:underline'>
                    Back to clients
                </Link>
            </div>
        )
    }

    const open = data.items.filter((i) => i.status !== 'completed')
    const completed = data.items.filter((i) => i.status === 'completed')

    return (
        <div className='max-w-5xl mx-auto px-4 sm:px-6 py-8'>
            <Link
                to={`/clients/${id}`}
                className='inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-neutral-900 mb-4'
            >
                <ArrowLeftIcon className='w-4 h-4' />
                Back to {data.childName ?? 'client overview'}
            </Link>

            <div className='flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6'>
                <div>
                    <h1 className='text-2xl sm:text-3xl font-bold text-neutral-900'>Homework</h1>
                    {data.childName && <p className='text-sm text-neutral-600 mt-1'>{data.childName}</p>}
                </div>
                {canManage && (
                    <button
                        onClick={() => setShowNew(true)}
                        className='inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg'
                    >
                        <PlusIcon className='w-4 h-4' />
                        Assign new homework
                    </button>
                )}
            </div>

            {showNew && (
                <NewHomeworkForm
                    licenseId={id!}
                    onAssigned={handleAssigned}
                    onCancel={() => setShowNew(false)}
                />
            )}

            <section className='mb-8'>
                <h2 className='text-base font-semibold text-neutral-800 mb-3'>Open ({open.length})</h2>
                {open.length === 0 ? (
                    <div className='text-sm text-neutral-500 bg-white border border-neutral-200 rounded-xl p-6 text-center'>
                        No open homework for this client.
                    </div>
                ) : (
                    <ul className='space-y-3'>
                        {open.map((h) => (
                            <HomeworkRow key={h._id} item={h} licenseId={id!} canManage={canManage} onCancelled={handleCancelled} />
                        ))}
                    </ul>
                )}
            </section>

            <section>
                <h2 className='text-base font-semibold text-neutral-800 mb-3'>Completed ({completed.length})</h2>
                {completed.length === 0 ? (
                    <div className='text-sm text-neutral-500'>Nothing completed yet.</div>
                ) : (
                    <ul className='space-y-3'>
                        {completed.map((h) => (
                            <HomeworkRow key={h._id} item={h} licenseId={id!} canManage={canManage} onCancelled={handleCancelled} />
                        ))}
                    </ul>
                )}
            </section>
        </div>
    )
}

function HomeworkRow({
    item,
    licenseId,
    canManage,
    onCancelled,
}: {
    item: HomeworkItem
    licenseId: string
    canManage: boolean
    onCancelled: (homeworkId: string) => void
}) {
    const status = displayStatus(item)
    const [cancelState, setCancelState] = useState<'idle' | 'confirm' | 'cancelling'>('idle')
    const [cancelError, setCancelError] = useState<string | null>(null)

    async function handleCancel() {
        setCancelError(null)
        setCancelState('cancelling')
        // suppressForbiddenRedirect: a role-denied 403 should show inline here,
        // not bounce the coordinator to /forbidden mid-task.
        const response = await api(
            `${ROUTES.provider.homework}/${licenseId}/${item._id}/cancel`,
            {},
            undefined,
            undefined,
            true
        )
        if (response.status && response.status < 400) {
            onCancelled(item._id)
        } else {
            setCancelState('idle')
            setCancelError(response.error || 'Failed to cancel homework')
        }
    }

    return (
        <li
            className={cn(
                'bg-white border rounded-xl p-4',
                status === 'overdue' ? 'border-red-200 bg-red-50/30' : 'border-neutral-200'
            )}
        >
            <div className='flex items-start gap-3'>
                <StatusIcon status={status} />
                <div className='flex-1 min-w-0'>
                    <div className='flex flex-wrap items-center gap-2 mb-1'>
                        <span className='text-sm font-medium text-neutral-900'>{item.title}</span>
                        <StatusBadge status={status} />
                    </div>
                    {item.description && (
                        <p className='text-xs text-neutral-600 mb-2 whitespace-pre-wrap'>{item.description}</p>
                    )}

                    <div className='flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-500'>
                        {item.assignedByName && (
                            <>
                                <span className='inline-flex items-center gap-1'>
                                    <UserCircleIcon className='w-3.5 h-3.5' />
                                    Assigned by{' '}
                                    <span className='font-medium text-neutral-700'>{item.assignedByName}</span>
                                </span>
                                <span>·</span>
                            </>
                        )}
                        {item.assignedAt && <span>Assigned {formatDate(item.assignedAt)}</span>}
                        {item.dueDate && (
                            <>
                                <span>·</span>
                                <span
                                    className={
                                        status === 'overdue'
                                            ? 'font-medium text-red-700'
                                            : status === 'completed'
                                              ? ''
                                              : 'font-medium text-neutral-700'
                                    }
                                >
                                    Due {formatDate(item.dueDate)}
                                </span>
                            </>
                        )}
                        {item.completedAt && (
                            <>
                                <span>·</span>
                                <span className='font-medium text-green-700'>
                                    Completed {formatDate(item.completedAt)}
                                </span>
                            </>
                        )}
                    </div>

                    {item.completionNote && (
                        <div className='mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-900 whitespace-pre-wrap'>
                            <span className='font-medium'>Family note on completion:</span> {item.completionNote}
                        </div>
                    )}

                    {cancelError && <p className='mt-2 text-xs text-red-600'>{cancelError}</p>}
                </div>

                {status !== 'completed' && canManage && (
                    <div className='flex-shrink-0'>
                        {cancelState === 'confirm' ? (
                            <div className='flex items-center gap-1.5'>
                                <button
                                    onClick={handleCancel}
                                    className='px-2.5 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg'
                                >
                                    Confirm cancel
                                </button>
                                <button
                                    onClick={() => setCancelState('idle')}
                                    className='px-2.5 py-1.5 text-xs font-medium text-neutral-600 hover:bg-neutral-100 rounded-lg'
                                >
                                    Keep
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={() => setCancelState('confirm')}
                                disabled={cancelState === 'cancelling'}
                                className='px-2.5 py-1.5 text-xs font-medium text-neutral-500 hover:text-red-700 hover:bg-red-50 rounded-lg disabled:opacity-50'
                            >
                                {cancelState === 'cancelling' ? 'Cancelling…' : 'Cancel'}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </li>
    )
}

function NewHomeworkForm({
    licenseId,
    onAssigned,
    onCancel,
}: {
    licenseId: string
    onAssigned: (item: HomeworkItem) => void
    onCancel: () => void
}) {
    const [title, setTitle] = useState('')
    const [description, setDescription] = useState('')
    const [dueDate, setDueDate] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    async function handleSubmit() {
        if (!title.trim()) {
            setError('A title is required')
            return
        }
        setError(null)
        setSubmitting(true)
        const body: { title: string; description?: string; dueDate?: string } = { title: title.trim() }
        if (description.trim()) body.description = description.trim()
        if (dueDate) body.dueDate = dueDate
        // suppressForbiddenRedirect: a role-denied 403 should show inline here,
        // not bounce the coordinator to /forbidden mid-task.
        const response = await api<HomeworkItem>(
            `${ROUTES.provider.homework}/${licenseId}`,
            body,
            undefined,
            undefined,
            true
        )
        setSubmitting(false)
        if (response.status && response.status < 400 && response.data) {
            onAssigned(response.data)
        } else {
            setError(response.error || 'Failed to assign homework')
        }
    }

    return (
        <div className='bg-white border-2 border-dashed border-brand-300 rounded-xl p-5 mb-6'>
            <h3 className='text-sm font-semibold text-neutral-900 mb-3'>Assign new homework</h3>
            <div className='space-y-2'>
                <input
                    type='text'
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    maxLength={200}
                    placeholder='Title (what should the family do?)'
                    className='w-full px-3 py-2 border border-neutral-300 rounded text-sm'
                />
                <textarea
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    maxLength={2000}
                    placeholder='Describe the task. Include any context from the visit if relevant.'
                    className='w-full px-3 py-2 border border-neutral-300 rounded text-sm'
                />
                <div className='flex items-center gap-2'>
                    <label className='text-xs text-neutral-600'>Due by</label>
                    <input
                        type='date'
                        value={dueDate}
                        onChange={(e) => setDueDate(e.target.value)}
                        className='px-3 py-1.5 border border-neutral-300 rounded text-sm'
                    />
                </div>
            </div>
            {error && <p className='mt-3 text-sm text-red-600'>{error}</p>}
            <div className='mt-4 flex items-center gap-2'>
                <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className='px-4 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg disabled:opacity-50'
                >
                    {submitting ? 'Assigning…' : 'Assign'}
                </button>
                <button
                    onClick={onCancel}
                    className='px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-100 rounded-lg'
                >
                    Cancel
                </button>
            </div>
        </div>
    )
}

function StatusIcon({ status }: { status: DisplayStatus }) {
    if (status === 'completed') {
        return (
            <div className='w-7 h-7 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0'>
                <CheckCircleIcon className='w-4 h-4 text-green-600' />
            </div>
        )
    }
    if (status === 'overdue') {
        return (
            <div className='w-7 h-7 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0'>
                <ExclamationTriangleIcon className='w-4 h-4 text-red-600' />
            </div>
        )
    }
    return <div className='w-7 h-7 rounded-full border-2 border-neutral-300 flex-shrink-0' />
}

function StatusBadge({ status }: { status: DisplayStatus }) {
    const styles: Record<DisplayStatus, string> = {
        assigned: 'bg-neutral-100 text-neutral-700',
        completed: 'bg-green-100 text-green-800',
        overdue: 'bg-red-100 text-red-800',
    }
    const labels: Record<DisplayStatus, string> = {
        assigned: 'Assigned',
        completed: 'Completed',
        overdue: 'Overdue',
    }
    return (
        <span
            className={cn(
                'inline-flex items-center text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded',
                styles[status]
            )}
        >
            {labels[status]}
        </span>
    )
}
